import { useCallback } from 'react';

import { DI_TOKENS } from '../../../constants';
import { useInjection } from '../../../hooks';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { AuthStore } from '../../../stores';

const POPUP_SUPPRESSION_PREFIX = 'orgProfileNudge:popupSuppressed';

/**
 * Per-user / per-org persistence for the New Assessment completion popup.
 *
 * The flag is stored in localStorage keyed by tenant + user, so it survives
 * sessions: once the popup is dismissed (or the user crosses the 1-field
 * threshold and the page suppresses it), it never reappears for that user/org.
 *
 * The Assessments Home banner deliberately has NO stored flag — its removal is
 * purely live-recalculated from the completion percentage (>=50% hides it
 * permanently), so no persistence hook is needed there.
 */
export function useProfileNudgeSuppression(): {
  popupSuppressed: boolean;
  suppressPopup: () => void;
} {
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const tenantId = authStore.userProfile?.tenant.id ?? 'anonymous';
  const userId = authStore.userProfile?.user.id ?? 'anonymous';

  const [popupSuppressed, setPopupSuppressed] = useLocalStorage<boolean>(
    `${POPUP_SUPPRESSION_PREFIX}:${tenantId}:${userId}`,
    false,
  );

  const suppressPopup = useCallback(
    () => setPopupSuppressed(true),
    [setPopupSuppressed],
  );

  return { popupSuppressed, suppressPopup };
}
